import React, { useState } from 'react';
import { P, mono } from '../styles/theme';
import { Card, SectionTitle, InfoRow } from '../components/Shared';

/**
 * Tab thông tin KSK theo TT 32/2023/TT-BYT (dữ liệu từ /api/kskresults/{maLuotKham}):
 * Hành chính → Tiền sử → Thể lực → Lâm sàng chuyên khoa → Kết luận.
 * Dùng chung Mẫu 01 / 03 (loaiMau chỉ ảnh hưởng vài field hiển thị).
 */

const NHOM_TIENSU = {
  BANTHAN: 'Tiền sử bản thân',
  GIADINH: 'Tiền sử gia đình',
  THUOC: 'Thuốc đang sử dụng',
  THAISAN: 'Tiền sử thai sản',
  NGHENGHIEP: 'Tiền sử nghề nghiệp',
};

const PHANLOAI_COLOR = {
  I: P.success, II: P.success, III: P.warning, IV: P.orange, V: P.danger,
};

/**
 * Panel Hành chính tách riêng — ResultPage render ngay dưới patient summary
 * (trong page-content, phía trên thanh tab) để tab nào cũng nhìn thấy.
 */
export function KskHanhChinhCard({ result }) {
  const patient = result.patient || {};
  const isMau03 = result.loaiMau === '03';
  return (
    <Card style={{ padding: '20px 24px' }}>
      <SectionTitle icon="👤" title="Hành chính" />
      <div className="ksk-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <InfoRow label="Họ tên" value={patient.name} />
        <InfoRow label="Ngày sinh" value={patient.dob} />
        <InfoRow label="Giới tính" value={patient.gender} />
        <InfoRow label="CCCD" value={patient.soCccd} useMono />
        <InfoRow label="SĐT" value={patient.phone} useMono />
        <InfoRow label="Mã lượt khám" value={result.maLuotKham} useMono />
        {patient.maNv ? <InfoRow label="Mã nhân viên" value={patient.maNv} useMono /> : null}
        {isMau03 && patient.tenKhachHang ? <InfoRow label="Đơn vị" value={patient.tenKhachHang} /> : null}
        {isMau03 && patient.boPhan ? <InfoRow label="Bộ phận" value={patient.boPhan} /> : null}
        {patient.ngheNghiep ? <InfoRow label="Nghề nghiệp" value={patient.ngheNghiep} /> : null}
        {patient.soBenhAn ? <InfoRow label="Số hồ sơ" value={patient.soBenhAn} useMono /> : null}
      </div>
      <div style={{ marginTop: 8 }}>
        <InfoRow label="Địa chỉ" value={patient.address} />
        {patient.lyDoKham ? <InfoRow label="Lý do khám" value={patient.lyDoKham} /> : null}
      </div>
    </Card>
  );
}

export default function TabKskInfo({ result }) {
  const theLuc = result.theLuc || {};
  const tienSu = result.tienSu || [];
  const lamSang = result.lamSang || [];
  const ketLuan = result.ketLuan || {};
  // Mặc định mở TẤT CẢ chuyên khoa; click chỉ để đóng/mở từng mục
  const [closedCk, setClosedCk] = useState(() => new Set());
  const toggleCk = (i) => setClosedCk(prev => {
    const s = new Set(prev);
    if (s.has(i)) s.delete(i); else s.add(i);
    return s;
  });

  const bmiVal = parseFloat(theLuc.bmi);
  const bmiColor = bmiVal >= 25 ? P.warning : bmiVal >= 23 ? P.orange : P.success;

  const vitalCards = [
    { label: 'Chiều cao', value: theLuc.chieuCao, unit: 'cm', color: P.primary },
    { label: 'Cân nặng', value: theLuc.canNang, unit: 'kg', color: P.primary },
    { label: 'BMI', value: theLuc.bmi, unit: '', color: bmiColor, sub: theLuc.bmiStatus },
    { label: 'Huyết áp', value: theLuc.huyetAp, unit: 'mmHg', color: parseInt(theLuc.huyetAp) >= 130 ? P.warning : P.success },
    { label: 'Mạch', value: theLuc.mach, unit: 'l/ph', color: P.success },
    { label: 'Nhiệt độ', value: theLuc.nhietDo, unit: '°C', color: P.success },
    { label: 'Nhịp thở', value: theLuc.nhipTho, unit: 'l/ph', color: P.success },
    { label: 'SpO2', value: theLuc.spo2, unit: '%', color: P.success },
  ].filter(v => v.value);

  // Gom tiền sử theo nhóm, giữ thứ tự stt
  const tienSuGrouped = tienSu.reduce((acc, t) => {
    (acc[t.nhom] = acc[t.nhom] || []).push(t);
    return acc;
  }, {});

  const plColor = PHANLOAI_COLOR[(ketLuan.phanLoaiSucKhoe || '').trim()] || P.primary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ===== II. Tiền sử ===== */}
      {tienSu.length > 0 && (
        <Card style={{ padding: '20px 24px' }}>
          <SectionTitle icon="📜" title="Tiền sử bệnh tật" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Object.entries(tienSuGrouped).map(([nhom, items]) => (
              <div key={nhom}>
                <div style={{ fontSize: 12, fontWeight: 700, color: P.textDim, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>
                  {NHOM_TIENSU[nhom] || nhom}
                </div>
                <div style={{ display: 'grid', gap: 4 }}>
                  {items.map((t, i) => (
                    <div key={i} style={{ fontSize: 13, display: 'flex', gap: 8 }}>
                      {t.tenMuc && <span style={{ color: P.textMuted, minWidth: 160 }}>{t.tenMuc}:</span>}
                      <span style={{ color: P.text, lineHeight: 1.5 }}>{t.noiDung || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ===== III. Thể lực ===== */}
      {vitalCards.length > 0 && (
        <Card style={{ padding: '20px 24px' }}>
          <SectionTitle icon="📏" title="Khám thể lực" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            {vitalCards.map((v, i) => (
              <div key={i} style={{ background: P.bg, borderRadius: 10, padding: '12px 14px', border: '1px solid ' + P.border }}>
                <div style={{ fontSize: 11, color: P.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5, fontWeight: 600 }}>{v.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: v.color, fontFamily: mono }}>
                  {v.value}{v.unit ? <span style={{ fontSize: 12, fontWeight: 500 }}> {v.unit}</span> : null}
                </div>
                {v.sub && <div style={{ fontSize: 11, color: v.color, marginTop: 2, fontWeight: 500 }}>{v.sub}</div>}
              </div>
            ))}
          </div>
          {(theLuc.phanLoaiTheLuc || theLuc.bacSy) && (
            <div style={{ marginTop: 12, fontSize: 13, color: P.textDim }}>
              {theLuc.phanLoaiTheLuc && <span>Phân loại thể lực: <strong style={{ color: P.text }}>{theLuc.phanLoaiTheLuc}</strong></span>}
              {theLuc.bacSy && <span style={{ marginLeft: 16 }}>BS khám: <strong style={{ color: P.text }}>{theLuc.bacSy}</strong></span>}
            </div>
          )}
        </Card>
      )}

      {/* ===== IV. Khám lâm sàng chuyên khoa ===== */}
      {lamSang.length > 0 && (
        <Card style={{ padding: '20px 24px' }}>
          <SectionTitle icon="🩺" title="Khám lâm sàng" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lamSang.map((ck, i) => {
              const isAbn = ck.phanLoai && !/bình thường|^I$/i.test(ck.phanLoai.trim());
              const open = !closedCk.has(i);
              return (
                <div key={i} style={{
                  border: '1px solid ' + (isAbn ? P.warning + '50' : P.border),
                  borderRadius: 10, overflow: 'hidden',
                  background: isAbn ? '#fef8f0' : P.white,
                }}>
                  <button onClick={() => toggleCk(i)} style={{
                    width: '100%', padding: '12px 16px', background: 'transparent', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontFamily: 'inherit',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: P.text }}>{ck.tenChuyenKhoa || ck.maChuyenKhoa}</span>
                      {ck.phanLoai && (
                        <span style={{
                          fontSize: 11, padding: '1px 8px', borderRadius: 6, fontWeight: 600,
                          background: (isAbn ? P.warning : P.success) + '20',
                          color: isAbn ? P.warning : P.success,
                        }}>{ck.phanLoai}</span>
                      )}
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={P.textMuted} strokeWidth="2"
                      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {open && (
                    <div style={{ padding: '0 16px 14px', fontSize: 13 }}>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {ck.ketQua && <div><span style={{ color: P.textMuted }}>Kết quả: </span><span style={{ color: P.text, lineHeight: 1.6 }}>{ck.ketQua}</span></div>}
                        {ck.bacSy && <div><span style={{ color: P.textMuted }}>Bác sĩ: </span><span style={{ color: P.text }}>{ck.bacSy}</span></div>}
                        {ck.ghiChu && (
                          <div style={{
                            padding: '8px 12px', background: P.primaryLight, borderRadius: 8,
                            border: '1px solid ' + P.primary + '20', color: P.primary, fontSize: 12,
                          }}>💡 {ck.ghiChu}</div>
                        )}
                        {(ck.chiSo || []).length > 0 && (
                          <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
                            <tbody>
                              {ck.chiSo.map((cs, j) => (
                                <tr key={j}>
                                  <td style={{ padding: '4px 16px 4px 0', color: P.textMuted, fontSize: 12 }}>{cs.tenChiSo}</td>
                                  <td style={{ padding: '4px 0', color: P.text, fontWeight: 600, fontFamily: mono, fontSize: 12 }}>{cs.giaTri}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ===== VI. Kết luận ===== */}
      <Card style={{ padding: '20px 24px', border: '1px solid ' + plColor + '40' }}>
        <SectionTitle icon="📋" title="Kết luận" />
        {ketLuan.phanLoaiSucKhoe ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 54, height: 54, borderRadius: 12,
              background: plColor + '15', border: '2px solid ' + plColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 800, color: plColor, fontFamily: mono,
            }}>{ketLuan.phanLoaiSucKhoe}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: plColor }}>
                Phân loại sức khỏe: Loại {ketLuan.phanLoaiSucKhoe}
              </div>
              {ketLuan.phanLoaiMoTa && <div style={{ fontSize: 13, color: P.textDim, marginTop: 2 }}>{ketLuan.phanLoaiMoTa}</div>}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: P.textMuted, marginBottom: 12 }}>Chưa có kết luận phân loại sức khỏe.</div>
        )}
        <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
          {ketLuan.benhTat && <div><span style={{ color: P.textMuted }}>Các bệnh, tật (nếu có): </span><span style={{ color: P.text, lineHeight: 1.6 }}>{ketLuan.benhTat}</span></div>}
          {ketLuan.kienNghi && <div><span style={{ color: P.textMuted }}>Đề nghị: </span><span style={{ color: P.text, lineHeight: 1.6 }}>{ketLuan.kienNghi}</span></div>}
          {(ketLuan.bacSyKetLuan || ketLuan.ngayKetLuan) && (
            <div style={{ marginTop: 6, color: P.textDim }}>
              {ketLuan.bacSyKetLuan && <span>BS kết luận: <strong style={{ color: P.text }}>{ketLuan.bacSyKetLuan}</strong></span>}
              {ketLuan.ngayKetLuan && <span style={{ marginLeft: 16 }}>Ngày: <strong style={{ color: P.text, fontFamily: mono }}>{ketLuan.ngayKetLuan}</strong></span>}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
